(function () {
  /* =============================================================
     View Transitions — Instagram/Facebook WebView Detection
     
     Instagram and Facebook in-app browsers have buggy View Transitions
     API implementations. The `@view-transition { navigation: auto; }` CSS
     rule in base.css enables cross-document view transitions for every
     navigation, creating a `::view-transition` overlay that persists in
     these WebViews and blocks all pointer events.
     
     This script:
     1. Detects Instagram/Facebook WebView early
     2. Injects a CSS override to disable @view-transition
     3. Skips all View Transitions JavaScript setup
     
     This must run BEFORE any navigation happens, so it's executed
     synchronously at the top of the IIFE.
     ============================================================= */

  function isInstagramOrFacebookWebView() {
    const ua = navigator.userAgent || '';
    // Instagram WebView
    if (ua.includes('Instagram')) return true;
    // Facebook WebView
    if (ua.includes('FBAN') || ua.includes('FBAV') || ua.includes('FB_IAB')) return true;
    return false;
  }

  // Detect Instagram/Facebook WebView and disable View Transitions entirely.
  // The @view-transition rule was removed from base.css and is only injected
  // below for supported browsers, so Instagram/Facebook WebView never gets
  // the rule and cross-document view transitions are never enabled.
  if (isInstagramOrFacebookWebView()) {
    // No-op: View Transitions are simply never enabled in these WebViews.
    return;
  }

  /* =============================================================
     Below: View Transitions setup for browsers that support it
     (Chrome, Safari, Brave — NOT Instagram/Facebook WebView)
     ============================================================= */

  // Feature detection: Check if View Transitions API is properly supported
  const supportsViewTransitions = typeof document.startViewTransition === 'function' &&
                                   typeof ViewTransition === 'function';

  // Only set up View Transitions if fully supported
  if (!supportsViewTransitions) {
    // Unsupported browser - skip View Transitions entirely
    return;
  }

  // Inject the @view-transition rule to enable cross-document View Transitions.
  // This rule was moved from base.css to here so it only runs in supported
  // browsers, preventing the ::view-transition overlay from persisting and
  // blocking pointer events in buggy WebViews (Instagram/Facebook).
  // It is wrapped in @media (prefers-reduced-motion: no-preference) to match
  // the original behavior from base.css.
  var vtStyle = document.createElement('style');
  vtStyle.id = 'view-transition-enable';
  vtStyle.textContent = '@media (prefers-reduced-motion: no-preference) { ' +
                        '  @view-transition { navigation: auto; } ' +
                        '}';
  (document.head || document.documentElement).appendChild(vtStyle);

  // Helper: Create a timeout promise
  function withTimeout(promise, timeoutMs, fallbackValue) {
    return Promise.race([
      promise,
      new Promise((resolve) => setTimeout(() => resolve(fallbackValue), timeoutMs))
    ]);
  }

  // Helper: Check if device is low power
  function isLowPowerDevice() {
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }

  // Helper: Check if user prefers reduced motion
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  // pageswap event: Configure view transition types before navigation
  window.addEventListener('pageswap', async (event) => {
    try {
      const { viewTransition } = event;

      // Skip if view transition is not valid or device is low power
      if (!viewTransition || !(viewTransition instanceof ViewTransition) || isLowPowerDevice()) {
        viewTransition?.skipTransition?.();
        return;
      }

      // Cancel view transition on user interaction to improve INP
      ['pointerdown', 'keydown'].forEach((eventName) => {
        document.addEventListener(
          eventName,
          () => {
            try { viewTransition.skipTransition(); } catch (e) { /* ignore */ }
          },
          { once: true }
        );
      });

      // Clean up view transition types
      document
        .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
        .forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });

      const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
      const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

      if (transitionType) {
        viewTransition.types.clear();
        viewTransition.types.add(transitionType);
        sessionStorage.setItem('custom-transition-type', transitionType);
      } else {
        viewTransition.types.clear();
        viewTransition.types.add('page-navigation');
        sessionStorage.removeItem('custom-transition-type');
      }
    } catch (error) {
      console.warn('[ViewTransitions] pageswap error:', error);
    }
  });

  // pagereveal event: Handle view transition after page is revealed
  window.addEventListener('pagereveal', async (event) => {
    try {
      const { viewTransition } = event;

      // Reset scroll position for forward navigations
      if (performance.navigation.type === performance.navigation.TYPE_NAVIGATE) {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }

      // Configure view transition types if supported
      if (viewTransition && viewTransition instanceof ViewTransition && !isLowPowerDevice()) {
        const customTransitionType = sessionStorage.getItem('custom-transition-type');

        if (customTransitionType) {
          viewTransition.types.clear();
          viewTransition.types.add(customTransitionType);

          // CRITICAL: Add timeout to prevent indefinite hanging
          await withTimeout(viewTransition.finished, 2000, null);

          viewTransition.types.clear();
          viewTransition.types.add('page-navigation');

          idleCallback(() => {
            sessionStorage.removeItem('custom-transition-type');
            document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
              element.removeAttribute('data-view-transition-type');
            });
          });
        } else {
          viewTransition.types.clear();
          viewTransition.types.add('page-navigation');
        }
      }

      // CRITICAL: Add timeout to prevent indefinite hanging
      await withTimeout(viewTransition?.finished, 2000, null);

      // Start Penny loading overlay
      (function tryStart() {
        if (typeof window.startPennyLoading === 'function') {
          window.startPennyLoading();
        } else {
          let retries = 0;
          const maxRetries = 60;
          requestAnimationFrame(function retry() {
            if (typeof window.startPennyLoading === 'function') {
              window.startPennyLoading();
            } else if (retries < maxRetries) {
              retries++;
              requestAnimationFrame(retry);
            }
          });
        }
      })();
    } catch (error) {
      console.warn('[ViewTransitions] pagereveal error:', error);
      if (typeof window.startPennyLoading === 'function') {
        window.startPennyLoading();
      }
    }
  });
})();
